import { Component, computed, DestroyRef, HostListener, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, filter, map, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { AuthService } from '../../core/services/auth.service';
import { ApiService } from '../../core/services/api.service';
import { UiPreferencesService } from '../../core/services/ui-preferences.service';
import { PmRealtimeService } from '../../core/services/pm-realtime.service';
import { NotificationsService } from '../../features/notifications/notifications.service';
import { BreadcrumbComponent, BreadcrumbCrumb } from '../../shared/breadcrumb/breadcrumb.component';
import { IssueDrawerComponent } from '../../shared/issue-drawer/issue-drawer.component';
import {
  filterNavSections,
  findNavItem,
  findNavSectionForRoute,
  findNavTitle,
  itemVisible,
  NAV_SECTIONS,
  NavItem,
  NavSection,
  QUICK_CREATE_ACTIONS,
  searchNavItems
} from '../nav.config';

interface IssueSearchHit {
  id: string;
  key: string;
  summary?: string;
}

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule,
    MatBadgeModule,
    MatSnackBarModule,
    BreadcrumbComponent,
    IssueDrawerComponent
  ],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss'
})
export class ShellComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly api = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationsApi = inject(NotificationsService);
  private readonly realtime = inject(PmRealtimeService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroy$ = new Subject<void>();
  private readonly searchQuery$ = new Subject<string>();
  readonly prefs = inject(UiPreferencesService);

  readonly unreadCount = signal(0);
  readonly issueSuggestions = signal<IssueSearchHit[]>([]);
  readonly routeContextLabel = signal<string | null>(null);

  readonly user = this.auth.user;
  readonly displayName = this.auth.displayName;
  readonly rolesLabel = this.auth.rolesLabel;

  readonly mobileOpen = signal(false);
  readonly searchQuery = signal('');
  readonly searchFocused = signal(false);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  readonly currentPath = computed(() => (this.url() || '/dashboard').split('?')[0]);

  readonly pageTitle = computed(() => {
    const path = this.currentPath();
    const ctx = this.routeContextLabel();
    if (ctx) return ctx;
    return findNavTitle(path);
  });

  readonly breadcrumbs = computed((): BreadcrumbCrumb[] => {
    const path = this.currentPath();
    const crumbs: BreadcrumbCrumb[] = [{ label: 'Workspace', route: '/dashboard' }];
    const section = findNavSectionForRoute(path);
    const item = findNavItem(path);

    if (section && section.id !== 'dashboard') {
      const sectionRoute = section.flat && section.items.length === 1
        ? section.items[0].route
        : section.items[0]?.route;
      crumbs.push({ label: section.label, route: sectionRoute });
    }

    const projectMatch = path.match(/^\/projects\/([^/]+)$/);
    if (projectMatch && projectMatch[1] !== 'new') {
      crumbs.push({ label: 'Projects', route: '/projects' });
      crumbs.push({ label: this.routeContextLabel() || projectMatch[1] });
      return crumbs;
    }

    const issueMatch = path.match(/^\/issues\/([^/]+)$/);
    if (issueMatch) {
      crumbs.push({ label: 'Backlog', route: '/issues' });
      crumbs.push({ label: this.routeContextLabel() || 'Issue' });
      return crumbs;
    }

    if (item && item.route !== '/dashboard') {
      crumbs.push({ label: item.label });
    } else if (!section || section.id === 'dashboard') {
      crumbs.push({ label: 'Dashboard' });
    }

    return crumbs;
  });

  readonly isCurrentFavorite = computed(() => this.prefs.isFavorite(this.currentPath()));

  readonly navSections = computed(() =>
    filterNavSections(NAV_SECTIONS, this.user()?.roles ?? [])
  );

  readonly expandedId = computed(() => this.prefs.expandedSectionId());

  readonly searchResults = computed(() =>
    searchNavItems(this.searchQuery(), this.user()?.roles ?? [])
  );

  readonly recentItems = computed(() => this.prefs.recentItems());

  readonly favoriteItems = computed(() => {
    const roles = this.user()?.roles ?? [];
    return this.prefs.favorites()
      .map((route) => findNavItem(route))
      .filter((item): item is NavItem => !!item && itemVisible(item, roles));
  });

  readonly quickCreate = computed(() => {
    const roles = this.user()?.roles ?? [];
    return QUICK_CREATE_ACTIONS.filter((a) => itemVisible(a as NavItem, roles));
  });

  readonly initials = computed(() => {
    const name = this.displayName() || 'U';
    return name
      .split(' ')
      .filter(Boolean)
      .map((p) => p.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  });

  readonly themeIcon = computed(() => {
    const mode = this.prefs.themeMode();
    if (mode === 'dark') return 'dark_mode';
    if (mode === 'system') return 'contrast';
    return 'light_mode';
  });

  readonly themeLabel = computed(() => {
    const mode = this.prefs.themeMode();
    if (mode === 'dark') return 'Dark';
    if (mode === 'system') return 'System';
    return 'Light';
  });

  readonly isMobile = signal(typeof window !== 'undefined' ? window.innerWidth < 960 : false);
  readonly isCollapsed = computed(() =>
    this.isMobile() ? false : this.prefs.sidebarCollapsed()
  );

  ngOnInit(): void {
    const fromRoute = findNavSectionForRoute(this.router.url);
    if (fromRoute) this.prefs.setExpandedSection(fromRoute.id);

    this.refreshUnreadCount();
    this.resolveRouteContext(this.router.url);
    void this.realtime.ensureNotificationsConnected().catch(() => undefined);

    this.realtime.notificationCreated$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      this.unreadCount.update(c => c + 1);
      this.snackBar.open(event.title, 'View', { duration: 5000 }).onAction().subscribe(() => {
        void this.router.navigateByUrl(event.actionUrl || '/notifications');
      });
    });

    this.searchQuery$.pipe(
      debounceTime(280),
      switchMap((q) => {
        const trimmed = q.trim();
        if (trimmed.length < 2) return of([] as IssueSearchHit[]);
        return this.api.get<IssueSearchHit[]>(`v1/pm/search/suggest?q=${encodeURIComponent(trimmed)}`).pipe(
          catchError(() => of([] as IssueSearchHit[]))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((hits) => this.issueSuggestions.set(Array.isArray(hits) ? hits : []));

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => {
        const item = findNavItem(e.urlAfterRedirects);
        if (item) {
          this.prefs.pushRecent(item.route, item.label);
          const section = findNavSectionForRoute(item.route);
          if (section) this.prefs.setExpandedSection(section.id);
        }
        this.resolveRouteContext(e.urlAfterRedirects);
        this.mobileOpen.set(false);
        this.searchQuery.set('');
        this.searchFocused.set(false);
        this.issueSuggestions.set([]);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveRouteContext(url: string): void {
    const path = url.split('?')[0];
    this.routeContextLabel.set(null);

    const projectMatch = path.match(/^\/projects\/([^/]+)$/);
    if (projectMatch && projectMatch[1] !== 'new') {
      const id = projectMatch[1];
      this.api.get<{ name?: string; Name?: string }>(`v1/projects/${id}`).pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$)
      ).subscribe((p) => {
        if (p) this.routeContextLabel.set(p.name ?? p.Name ?? id);
      });
      return;
    }

    const issueMatch = path.match(/^\/issues\/([^/]+)$/);
    if (issueMatch) {
      const id = issueMatch[1];
      this.api.get<{ key?: string; Key?: string; summary?: string; Summary?: string }>(`v1/issues/${id}`).pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$)
      ).subscribe((i) => {
        if (i) {
          const key = i.key ?? i.Key;
          const summary = i.summary ?? i.Summary;
          this.routeContextLabel.set(key ? (summary ? `${key}` : key) : 'Issue');
        }
      });
    }
  }

  refreshUnreadCount(): void {
    this.notificationsApi.unreadCount().subscribe({
      next: (count) => this.unreadCount.set(count),
      error: () => this.unreadCount.set(0)
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    const mobile = window.innerWidth < 960;
    this.isMobile.set(mobile);
    if (!mobile) this.mobileOpen.set(false);
    if (window.innerWidth < 1200 && window.innerWidth >= 960) {
      this.prefs.sidebarCollapsed.set(true);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    const inInput = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;

    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      document.getElementById('shell-search')?.focus();
      this.searchFocused.set(true);
    }

    if (event.key === 'Escape') {
      this.searchFocused.set(false);
      this.searchQuery.set('');
      if (this.isMobile()) this.mobileOpen.set(false);
    }

    if (!inInput && event.key === '?' && event.shiftKey) {
      event.preventDefault();
      this.showShortcutsHelp();
    }
  }

  showShortcutsHelp(): void {
    this.snackBar.open(
      'Ctrl+K — Search · Esc — Close panels · ? — Shortcuts help',
      'Dismiss',
      { duration: 8000, panelClass: 'tt-shortcuts-snackbar' }
    );
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    this.searchQuery$.next(value);
  }

  toggleCurrentFavorite(): void {
    this.prefs.toggleFavorite(this.currentPath());
  }

  isSectionExpanded(section: NavSection): boolean {
    return this.expandedId() === section.id;
  }

  toggleSection(section: NavSection, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isCollapsed()) {
      this.prefs.sidebarCollapsed.set(false);
    }

    if (section.flat && section.items.length === 1) {
      void this.router.navigateByUrl(section.items[0].route);
      this.prefs.setExpandedSection(section.id);
      return;
    }

    this.prefs.toggleExpandedSection(section.id);
  }

  sectionHasActive(section: NavSection): boolean {
    const path = this.currentPath();
    return section.items.some(
      (item) => item.route === path || path.startsWith(item.route + '/')
    );
  }

  toggleSidebar(): void {
    if (this.isMobile()) {
      this.mobileOpen.update((v) => !v);
      return;
    }
    this.prefs.toggleSidebarCollapsed();
  }

  closeMobile(): void {
    this.mobileOpen.set(false);
  }

  goSearchResult(item: NavItem): void {
    void this.router.navigateByUrl(item.route);
  }

  goRecent(item: { route: string; label: string }): void {
    void this.router.navigateByUrl(item.route);
  }

  goIssueSuggestion(hit: IssueSearchHit): void {
    void this.router.navigate(['/issues', hit.id]);
  }

  logout(): void {
    this.auth.logout();
  }
}
